import React, { useContext, useEffect } from "react";
import { LoadingIcon, TopDropdown } from "../../../../components";
import { UserContext } from "../../../../context/UserContext";
import { useLocalStorage } from "../../../../hooks";

const VisualisationDropdowns = ({
  siteMetadata,
  setSiteMetadata,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSite,
  setCurrentSite,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentPlot,
  setCurrentPlot,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  currentGreenhouse,
  setCurrentGreenhouse,
  currentDate,
  setCurrentDate,
  plotData,
  setPlotData,
  greenhouseData,
  setGreenhouseData,
  setDropdownLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  dateData,
  setDateData,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);

  // ----------------------------------------------------------
  // Sync loading state for entire dropdown group
  // ----------------------------------------------------------
  useEffect(() => {
    setDropdownLoading(orgLoading || siteLoading || cropLoading);
  }, [orgLoading, siteLoading, cropLoading, setDropdownLoading]);

  // ----------------------------------------------------------
  //  1) Org: parse from URL or localStorage or fallback
  // ----------------------------------------------------------
  useEffect(() => {
    if (orgData?.length) {
      const params = new URLSearchParams(searchParams);
      const orgIdFromUrl = params.get("org");

      if (orgIdFromUrl) {
        // check if it exists
        const foundOrg = orgData.find((o) => o.id === orgIdFromUrl);
        setCurrentOrg(foundOrg?.id ?? orgData[0].id);
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      } else if (!settingDefaults.org) {
        // localStorage
        const storedOrgId = defaultDropdown?.org;
        setCurrentOrg(storedOrgId ?? orgData[0].id);
        setSettingDefaults((prev) => ({ ...prev, org: true }));
      } else {
        // fallback
        setCurrentOrg(orgData[0].id);
      }
    }
  }, [orgData]);

  // whenever currentOrg changes => store in localStorage + URL param
  useEffect(() => {
    if (currentOrg) {
      const params = new URLSearchParams(searchParams);
      params.set("org", currentOrg);
      setSearchParams(params);

      setDefaultDropdown({
        ...defaultDropdown,
        org: currentOrg, // store ID in localStorage
      });
    }
  }, [currentOrg]);

  // ----------------------------------------------------------
  //  2) Site: parse from URL or localStorage or fallback
  // ----------------------------------------------------------
  useEffect(() => {
    if (siteData?.length && currentOrg) {
      const params = new URLSearchParams(searchParams);
      const siteIdFromUrl = params.get("site");

      if (siteIdFromUrl) {
        // check if it exists
        const foundSite = siteData.find((s) => s.id === siteIdFromUrl);
        setCurrentSite(foundSite ?? siteData[0]);
      } else if (!settingDefaults.site) {
        // localStorage
        const storedSiteId = defaultDropdown?.[currentOrg]?.site;
        if (storedSiteId) {
          const foundSite = siteData.find((s) => s.id === storedSiteId);
          setCurrentSite(foundSite ?? siteData[0]);
        } else {
          setCurrentSite(siteData[0]);
        }
        setSettingDefaults((prev) => ({ ...prev, site: true }));
      } else {
        setCurrentSite(siteData[0]);
      }
    }
  }, [siteData, currentOrg]);

  // whenever currentSite changes => store in localStorage + URL param
  useEffect(() => {
    if (currentSite) {
      const params = new URLSearchParams(searchParams);
      params.set("site", currentSite.id);
      setSearchParams(params);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          site: currentSite.id, // store ID
        },
      });
    }
  }, [currentSite]);

  // ----------------------------------------------------------
  //  3) Crop: parse from URL or localStorage or fallback
  // ----------------------------------------------------------
  useEffect(() => {
    if (cropData?.length && currentSite) {
      const params = new URLSearchParams(searchParams);
      const cropIdFromUrl = params.get("crop");

      if (cropIdFromUrl) {
        const foundCrop = cropData.find((c) => c.id === cropIdFromUrl);
        setCurrentCrop(foundCrop ?? cropData[0]);
      } else if (!settingDefaults.crop) {
        // localStorage
        const storedCropId = defaultDropdown?.[currentOrg]?.crop;
        if (storedCropId) {
          const foundCrop = cropData.find((c) => c.id === storedCropId);
          setCurrentCrop(foundCrop ?? cropData[0]);
        } else {
          setCurrentCrop(cropData[0]);
        }
        setSettingDefaults((prev) => ({ ...prev, crop: true }));
      } else {
        setCurrentCrop(cropData[0]);
      }
    }
  }, [cropData, currentSite]);

  // whenever currentCrop changes => store in localStorage + URL param
  useEffect(() => {
    if (currentCrop) {
      const params = new URLSearchParams(searchParams);
      params.set("crop", currentCrop.id);
      setSearchParams(params);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          crop: currentCrop.id, // store ID
        },
      });
    }
  }, [currentCrop]);

  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------
  return (
    <>
      {orgLoading || siteLoading || cropLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns sm:mb-4">
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[150px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[150px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />
        </div>
      )}
    </>
  );
};

export default VisualisationDropdowns;
